import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, Lock } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { pub, money, errMsg } from "@/lib/api";
import { unlockAudio } from "@/lib/sound";

export const TotalsBlock = ({ t, testPrefix = "checkout" }) => (
  <dl className="space-y-1.5 text-sm" data-testid={`${testPrefix}-totals`}>
    <div className="flex justify-between"><dt>Subtotal</dt><dd data-testid={`${testPrefix}-subtotal`}>{money(t.subtotal_cents)}</dd></div>
    {t.convenience_fee_cents > 0 && <div className="flex justify-between"><dt>Convenience fee</dt><dd data-testid={`${testPrefix}-convenience-fee`}>{money(t.convenience_fee_cents)}</dd></div>}
    {t.charge_gst && <div className="flex justify-between"><dt>GST ({t.gst_rate}%)</dt><dd data-testid={`${testPrefix}-gst`}>{money(t.gst_cents)}</dd></div>}
    <div className="flex justify-between border-t border-line pt-2 font-heading text-lg font-black"><dt>Total (CAD)</dt><dd data-testid={`${testPrefix}-total`}>{money(t.total_cents)}</dd></div>
  </dl>
);

const CartLine = ({ l, onQty }) => (
  <li className="flex items-start justify-between gap-3 py-3" data-testid="cart-line">
    <div>
      <div className="font-semibold">{l.name}</div>
      {l.labels.length > 0 && <div className="text-xs text-muted2">{l.labels.join(", ")}</div>}
      <div className="text-sm">{money(l.unit_cents * l.qty)}</div>
    </div>
    <div className="flex items-center gap-2 rounded-full border border-line px-2 py-1">
      <button aria-label="Less" onClick={() => onQty(l.key, l.qty - 1)} data-testid="cart-line-minus"><Minus size={14} /></button>
      <span className="w-5 text-center text-sm font-bold">{l.qty}</span>
      <button aria-label="More" onClick={() => onQty(l.key, l.qty + 1)} data-testid="cart-line-plus"><Plus size={14} /></button>
    </div>
  </li>
);

export const CartSheet = ({ open, onOpenChange, venue, cart, setQty }) => {
  const [quote, setQuote] = useState(null);
  const [name, setName] = useState(localStorage.getItem("lg_name") || "");
  const [email, setEmail] = useState(localStorage.getItem("lg_email") || "");
  const [phone, setPhone] = useState(localStorage.getItem("lg_phone") || "");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  const payload = cart.map((l) => ({ item_id: l.item_id, qty: l.qty, choice_ids: l.choice_ids }));

  useEffect(() => {
    if (!open || !cart.length) { setQuote(null); return; }
    pub.post(`/venues/${venue.slug}/quote`, { items: payload }).then((r) => setQuote(r.data)).catch((e) => toast.error(errMsg(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, JSON.stringify(payload)]);

  const belowMin = quote && quote.totals.subtotal_cents < (venue.min_order_cents || 0);
  const pay = async () => {
    if (!name.trim()) return toast.error("Please enter your first name");
    unlockAudio();
    setBusy(true);
    localStorage.setItem("lg_name", name.trim());
    localStorage.setItem("lg_email", email.trim());
    localStorage.setItem("lg_phone", phone.trim());
    try {
      const r = await pub.post(`/venues/${venue.slug}/checkout`, { customer_name: name.trim(), email: email.trim() || null, phone: phone.trim() || null, items: payload, origin_url: window.location.origin });
      if (r.data.checkout_url) window.location.href = r.data.checkout_url;
      else { localStorage.removeItem(`lg_cart_${venue.slug}`); nav(`/o/${r.data.order_id}`); }
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="max-h-[92vh] overflow-y-auto rounded-t-3xl sm:mx-auto sm:max-w-lg" data-testid="cart-sheet">
        <SheetHeader className="text-left">
          <SheetTitle className="font-heading text-2xl font-black">Your order</SheetTitle>
          <SheetDescription>{venue.name} · ready in about {venue.prep_minutes} min</SheetDescription>
        </SheetHeader>
        {venue.practice && <div className="mt-3 rounded-2xl border-2 border-dashed border-ink p-3 text-center text-sm font-black" data-testid="checkout-practice-notice">
          Practice order: no real payment. Use test card 4242 4242 4242 4242.</div>}
        <ul className="mt-2 divide-y divide-line">{cart.map((l) => <CartLine key={l.key} l={l} onQty={setQty} />)}</ul>
        <div className="mt-4 grid gap-3">
          <div><Label htmlFor="cust-name">First name (so staff can call you)</Label>
            <Input id="cust-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={40} data-testid="checkout-name-input" /></div>
          <div><Label htmlFor="cust-email">Email for receipt (optional)</Label>
            <Input id="cust-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="checkout-email-input" /></div>
          {venue.manual && <div><Label htmlFor="cust-phone">Phone (optional)</Label>
            <Input id="cust-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="checkout-phone-input" /></div>}
        </div>
        <div className="mt-5 rounded-2xl bg-brand-pale p-4">{quote ? <TotalsBlock t={quote.totals} /> : <div className="text-sm">Calculating…</div>}
          {venue.manual && <div className="mt-3 rounded-xl bg-white p-2 text-center font-heading font-black" data-testid="checkout-pay-at-pickup">Pay at pickup — cash, card or e-Transfer at the counter</div>}</div>
        {belowMin && <p className="mt-3 text-sm font-semibold text-danger" data-testid="checkout-min-order-error">Minimum order is {money(venue.min_order_cents)} before tax.</p>}
        <button className="btn-primary mt-5 w-full !py-4 text-lg" disabled={busy || !quote || belowMin || !venue.accepting_orders} onClick={pay} data-testid="checkout-pay-btn">
          {venue.manual ? (busy ? "Placing order…" : "Place order") : <><Lock size={18} /> {busy ? "Opening secure checkout…" : `Pay ${quote ? money(quote.totals.total_cents) : ""}`}</>}
        </button>
        <p className="mt-3 text-center text-xs text-muted2">{venue.manual ? "No card needed now · the venue confirms your order first" : "Card, Apple Pay or Google Pay via Stripe"} · By ordering you agree to the LineGone{" "}
          <Link to="/terms" className="link-lg" data-testid="checkout-terms-link">Terms</Link> and <Link to="/privacy" className="link-lg" data-testid="checkout-privacy-link">Privacy Policy</Link>.</p>
      </SheetContent>
    </Sheet>
  );
};
